// client/src/context/NotificationContext.tsx
import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { NotificationState } from '../types/contexts';

// Define a notification item interface
export interface NotificationItem {
  id: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  read: boolean;
  timestamp: Date;
}

type Action =
  | { type: 'SHOW_NOTIFICATION'; payload: Omit<NotificationState, 'show'> }
  | { type: 'HIDE_NOTIFICATION' }
  | { type: 'ADD_NOTIFICATION'; payload: Omit<NotificationItem, 'id' | 'timestamp' | 'read'> }
  | { type: 'MARK_AS_READ'; payload: { id: string } }
  | { type: 'MARK_ALL_AS_READ' }
  | { type: 'REMOVE_NOTIFICATION'; payload: { id: string } };

interface NotificationContextType {
  notification: NotificationState;
  notifications: NotificationItem[];
  showNotification: (message: string, type: NotificationState['type'], duration?: number) => void;
  hideNotification: () => void;
  addNotification: (message: string, type: NotificationItem['type']) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  unreadCount: number;
}

interface NotificationReducerState {
  toast: NotificationState;
  notifications: NotificationItem[];
}

const initialState: NotificationReducerState = {
  toast: {
    show: false,
    message: '',
    type: 'info',
    duration: 3000
  },
  notifications: []
};

function notificationReducer(state: NotificationReducerState, action: Action): NotificationReducerState {
  switch (action.type) {
    case 'SHOW_NOTIFICATION':
      return {
        ...state,
        toast: {
          ...state.toast,
          show: true,
          ...action.payload
        }
      };
    case 'HIDE_NOTIFICATION':
      return {
        ...state,
        toast: {
          ...state.toast,
          show: false
        }
      };
    case 'ADD_NOTIFICATION':
      const newNotification: NotificationItem = {
        id: Date.now().toString(),
        ...action.payload,
        read: false,
        timestamp: new Date()
      };
      return {
        ...state,
        notifications: [newNotification, ...state.notifications]
      };
    case 'MARK_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload.id
            ? { ...notification, read: true }
            : notification
        )
      };
    case 'MARK_ALL_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification => ({
          ...notification,
          read: true
        }))
      };
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(
          notification => notification.id !== action.payload.id
        )
      };
    default:
      return state;
  }
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Local storage key for persisting notifications
const STORAGE_KEY = 'utilities_crm_notifications';

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(notificationReducer, initialState, () => {
    // Load persisted notifications from localStorage on init
    try {
      const savedNotifications = localStorage.getItem(STORAGE_KEY);
      if (savedNotifications) {
        const parsedNotifications = JSON.parse(savedNotifications);
        // Convert string timestamps back to Date objects
        const processedNotifications = parsedNotifications.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
        return {
          toast: initialState.toast,
          notifications: processedNotifications
        };
      }
    } catch (error) {
      console.error('Failed to load notifications from localStorage:', error);
    }
    return initialState;
  });
  
  // Save notifications to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.notifications));
    } catch (error) {
      console.error('Failed to save notifications to localStorage:', error);
    }
  }, [state.notifications]);
  
  const hideNotification = useCallback(() => {
    dispatch({ type: 'HIDE_NOTIFICATION' });
  }, []);

  const showNotification = useCallback((message: string, type: NotificationState['type'], duration = 3000) => {
    dispatch({
      type: 'SHOW_NOTIFICATION',
      payload: { message, type, duration }
    });

    // Auto-hide notification after duration
    setTimeout(hideNotification, duration);
    
    // Also add to persistent notifications if it's not just a temporary message
    if (duration > 2000) {
      dispatch({
        type: 'ADD_NOTIFICATION',
        payload: { message, type }
      });
    }
  }, [hideNotification]);
  
  const addNotification = useCallback((message: string, type: NotificationItem['type']) => {
    dispatch({
      type: 'ADD_NOTIFICATION',
      payload: { message, type }
    });
  }, []);
  
  const markAsRead = useCallback((id: string) => {
    dispatch({
      type: 'MARK_AS_READ',
      payload: { id }
    });
  }, []);
  
  const markAllAsRead = useCallback(() => {
    dispatch({ type: 'MARK_ALL_AS_READ' });
  }, []);
  
  const removeNotification = useCallback((id: string) => {
    dispatch({
      type: 'REMOVE_NOTIFICATION',
      payload: { id }
    });
  }, []);
  
  // Calculate unread count
  const unreadCount = state.notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notification: state.toast,
        notifications: state.notifications,
        showNotification,
        hideNotification,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        unreadCount
      }}
    >
      {children}
      {state.toast.show && (
        <div className={`notification-container ${state.toast.type}`}>
          <div className="notification-content">
            {state.toast.message}
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
}

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Custom hook to create a test notification (for development)
export const useCreateTestNotification = () => {
  const context = useNotification();
  
  return (type: NotificationItem['type'] = 'info') => {
    const messages = {
      info: 'This is a test information notification',
      success: 'Operation completed successfully',
      warning: 'Please be aware of this warning',
      error: 'An error has occurred'
    };
    
    context.addNotification(messages[type], type);
  };
};